// GanitaGrid.jsx
import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUndo, faLightbulb, faCheckCircle, faQuestionCircle, faTimes } from '@fortawesome/free-solid-svg-icons';

export default function GanitaGrid({ gridSize = 5, youtubeLink }) {
  const [showHelp, setShowHelp] = useState(false);
  const [notification, setNotification] = useState(null);
  const [completed, setCompleted] = useState(false);

  // Initialize empty grid
  const initGrid = () => Array(gridSize).fill(null).map(() => Array(gridSize).fill(''));

  const [grid, setGrid] = useState(initGrid());
  const [history, setHistory] = useState([]);

  const handleChange = (r, c, value) => {
    if (!/^\d*$/.test(value)) return; // only allow digits
    const newGrid = grid.map(row => [...row]);
    newGrid[r][c] = value;
    setHistory([...history, grid]);
    setGrid(newGrid);
  };

  const undo = () => {
    if (history.length === 0) return;
    const last = history[history.length - 1];
    setHistory(history.slice(0, -1));
    setGrid(last);
  };

  const giveHint = () => {
    // For demo, fill a random empty cell with '1'
    const emptyCells = [];
    for (let r = 0; r < gridSize; r++) {
      for (let c = 0; c < gridSize; c++) {
        if (!grid[r][c]) emptyCells.push([r, c]);
      }
    }
    if (emptyCells.length === 0) return;
    const [r, c] = emptyCells[Math.floor(Math.random() * emptyCells.length)];
    handleChange(r, c, '1');
  };

  const checkSolution = () => {
    // Dummy check for demo
    const isCorrect = true; // Replace with actual sum-check logic
    setCompleted(isCorrect);
    if (!isCorrect) {
      setNotification({ type: 'error', message: '❌ Not correct yet. Keep trying!' });
      setTimeout(() => setNotification(null), 3000);
    }
  };

  return (
    <div className="p-2 min-h-screen bg-black text-gray-300 flex flex-col items-center">

      {/* Top Buttons */}
      <div className="w-full flex justify-center gap-4 items-center mb-4">
        <button
          className="w-20 text-xs lg:text-m lg:w-40 px-4 py-2 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600 flex justify-center items-center gap-2"
          onClick={() => window.history.back()}
        >
          ← Back
        </button>

        <button
          className="w-20 text-xs lg:text-m lg:w-40 px-4 py-2 bg-blue-600 text-black rounded-lg hover:bg-blue-500 flex justify-center items-center gap-2"
          onClick={() => setShowHelp(true)}
        >
          <FontAwesomeIcon icon={faQuestionCircle} /> Help
        </button>
      </div>

      {/* Help Modal */}
      {showHelp && (
        <div className="fixed inset-0 bg-black bg-opacity-80 flex justify-center items-center z-50 p-4">
          <div className="bg-gray-900 p-6 rounded-xl max-w-lg w-full text-gray-300 flex flex-col gap-4">
            <h3 className="text-m lg:text-xl font-bold text-green-400">📖 How to Play Ganita Grid</h3>
            <p className='text-m lg:text-lg'>
              Ganita Grid (Kakuro-style) challenges you to fill the grid with numbers so that the sums in each row and column match the given clues. Each number can only appear once per sum group.
            </p>
            <ul className="list-disc list-inside text-gray-400">
              <li>Tap a cell and enter a number.</li>
              <li>Use hints to fill one cell correctly.</li>
              <li>Undo previous moves with the Undo button.</li>
              <li>Press Check when you think the puzzle is complete.</li>
            </ul>

            {youtubeLink && (
              <a
                href={youtubeLink}
                target="_blank"
                rel="noopener noreferrer"
                className="w-20 text-xs lg:text-m lg:w-40 px-4 py-2 bg-red-600 text-black rounded-lg hover:bg-red-500 text-center font-semibold"
              >
                ▶ Watch
              </a>
            )}

            <button
              className="w-20 text-xs lg:text-m lg:w-40 px-4 py-2 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600 mt-2"
              onClick={() => setShowHelp(false)}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {completed && (
        <div className="text-green-400 text-xl flex items-center gap-2 mb-2 animate-pulse">
          <FontAwesomeIcon icon={faCheckCircle} /> Puzzle Completed!
        </div>
      )}

      <h2 className="text-lg lg:text-2xl font-bold mb-2 text-green-400">🧮 Ganita Grid (Kakuro)</h2>
      <p className="text-gray-400 mb-4 text-center max-w-md text-xs lg:text-m">
        Fill numbers to satisfy the row and column sums. Use hints and undo to complete the puzzle!
      </p>

      {/* Grid */}
      <div className="grid grid-cols-[repeat(5,3rem)] gap-1">
        {grid.map((row, rIdx) =>
          row.map((cell, cIdx) => (
            <input
              key={`${rIdx}-${cIdx}`}
              value={cell}
              onChange={(e) => handleChange(rIdx, cIdx, e.target.value)}
              className="w-12 h-12 text-center text-black font-bold rounded bg-gray-200 focus:outline-none focus:ring-2 focus:ring-green-500"
            />
          ))
        )}
      </div>

      {/* Buttons */}
      <div className="p-4 flex flex-row gap-4 flex-wrap justify-center mt-4">
        <button
          onClick={checkSolution}
          className="w-20 text-xs lg:text-m lg:w-40 px-2 py-2 bg-green-600 text-black font-semibold rounded-lg hover:bg-green-500 transition flex justify-center items-center gap-2"
        >
          <FontAwesomeIcon icon={faCheckCircle} /> Check
        </button>
        <button
          onClick={undo}
          className="w-20 text-xs lg:text-m lg:w-40 px-2 py-2 bg-gray-700 text-gray-300 font-semibold rounded-lg hover:bg-gray-600 transition flex justify-center items-center gap-2"
        >
          <FontAwesomeIcon icon={faUndo} /> Undo
        </button>
        <button
          onClick={giveHint}
          className="w-20 text-xs lg:text-m lg:w-40 px-2 py-2 bg-blue-600 text-black font-semibold hover:bg-blue-500 transition flex justify-center items-center gap-2"
        >
          <FontAwesomeIcon icon={faLightbulb} /> Hint
        </button>
      </div>

      {/* Notification */}
      {notification && (
        <div
          className={`fixed top-4 left-1/2 transform -translate-x-1/2 px-4 py-2 rounded-lg text-sm font-semibold ${
            notification.type === 'error' ? 'bg-red-700 text-white' : 'bg-green-600 text-black'
          } flex justify-between items-center shadow-lg z-50 w-full max-w-md`}
        >
          <span>{notification.message}</span>
          <button
            className="w-10 p-1 text-lg flex items-center justify-center bg-red-800"
            onClick={() => setNotification(null)}
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
      )}

    </div>
  );
}
